import type { JSX } from 'react';
import { FormSection, Radio } from '@ksyos/design-system';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { FollowUpMoment } from '@common/customControls/bariatrics';
import type { IProcessActivity } from '@common/interfaces';
import type { BariPostSecretarialAssistantFollowUpData } from '@common/validation/customControls/bariPost/bariPostSecretarialAssistentFollowUp/v1/bariPostSecretarialAssistentFollowUpSchemaV1';
import { PatientPresence } from '@common/customControls/bariatrics';
import { CustomControlCode, ProcessActivityStatus, ServiceCode } from '@common/enums';
import { findSingleProcessByServiceCode } from '@common/utils/processUtil';
import { useProcessActivityContext, useProcessActivityEditContext } from '@frontend/utils';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { BmiFormGroup } from '../../BmiProcessActivityFormGroupFields';
import { ProcessActivityAssetsDropzone } from '../../processActivityAssets/ProcessActivityAssetsDropzone';
import {
    NumberInputColumnRowFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
} from '../../processActivityFormGroupFields';
import { BariPostSurgeryDateFormGroup } from '../BariPostSurgeryDateFormGroup';
import { initialData } from './bariPostSecretarialAssistantFollowUpData';
import { determineFollowUpMoments } from './determineFollowUpMoments';

export function BariPostSecretarialAssistantFollowUpEdit(): JSX.Element {
    const { data, errors, onChange, processActivityId } =
        useProcessActivityEditContext<BariPostSecretarialAssistantFollowUpData>();
    const { processData } = useProcessActivityContext<ServiceCode.BariPost>();
    const { processes } = usePatientEpisodeCategoryContext();
    const [followUpMoments, setFollowUpMoments] = useState<FollowUpMoment[]>([]);

    useEffect(() => {
        loadFollowUpMoments(processActivityId);
    }, [processes, processActivityId]);

    return (
        <>
            <FormSection title="Aanwezigheid">
                <BariPostSurgeryDateFormGroup />
                <RadioGroupFormGroup
                    fieldKey="moment"
                    isRequired={true}
                    label="Follow-up gesprek"
                >
                    {followUpMoments.map((followUpMoment) => (
                        <Radio
                            key={followUpMoment}
                            label={`Na ${followUpMoment}`}
                            value={followUpMoment}
                        />
                    ))}
                </RadioGroupFormGroup>
                <RadioGroupFormGroup
                    fieldKey="aanwezigheid"
                    isRequired={true}
                    label="De patiënt was aanwezig bij de afspraak"
                    onChange={handlePatientPresenceChange}
                    radioGroupLayout="row"
                >
                    <Radio
                        label="Ja"
                        value={PatientPresence.present}
                    />
                    <Radio
                        label="Nee"
                        value={PatientPresence.notPresent}
                    />
                </RadioGroupFormGroup>
                {data.aanwezigheid === PatientPresence.notPresent && (
                    <TextAreaFormGroup
                        fieldKey="toelichting_no_show"
                        isRequired={true}
                        label="Toelichting no-show"
                    />
                )}
            </FormSection>
            {data.aanwezigheid === PatientPresence.present && (
                <>
                    <FormSection title="Labwaarden">
                        <ProcessActivityAssetsDropzone
                            errorText="Voeg alsjeblieft bestanden met labwaarden toe"
                            isRequired={true}
                            label="Labuitslag"
                        />
                        <TextAreaFormGroup
                            fieldKey="bijzonderheden_labuitslag"
                            isRequired={false}
                            label="Bijzonderheden labuitslag"
                        />
                    </FormSection>
                    <FormSection title="Biometrische gegevens">
                        <BmiFormGroup
                            errors={errors}
                            fieldKey={{ height: 'lengte_in_cm', weight: 'gewicht_in_kg' }}
                            height={data.lengte_in_cm}
                            isRequired={true}
                            weight={data.gewicht_in_kg}
                        />
                        <NumberInputColumnRowFormGroup
                            appendedLabel="cm"
                            fieldKey="buikomvang"
                            hasErrors={errors.has('buikomvang')}
                            isRequired={true}
                            label="Buikomvang"
                        />
                    </FormSection>
                </>
            )}
        </>
    );

    async function handlePatientPresenceChange(value: string | null): Promise<void> {
        if (value === PatientPresence.present) {
            const changedData = {
                aanwezigheid: PatientPresence.present,
                lengte_in_cm: processData?.lengte_in_cm,
                toelichting_no_show: null,
            };
            onChange(changedData, Object.keys(changedData));
        } else {
            onChange(
                {
                    ...initialData,
                    aanwezigheid: PatientPresence.notPresent,
                    moment: data.moment,
                },
                Object.keys(initialData).filter((key) => !['toelichting_no_show', 'moment'].includes(key))
            );
        }
    }

    function loadFollowUpMoments(currentProcessActivityId: string | undefined): void {
        const bariPostProcess = findSingleProcessByServiceCode(processes, ServiceCode.BariPost);
        const followUpActivities = sortBy(
            bariPostProcess?.activities.filter(
                (activity) =>
                    activity.serviceActivity.customControlCode ===
                        CustomControlCode.BariPostSecretarialAssistentFollowUp &&
                    activity.status === ProcessActivityStatus.complete
            ),
            'performedDatetime'
        ) as unknown as IProcessActivity<BariPostSecretarialAssistantFollowUpData>[];

        const currentFollowUpActivity = followUpActivities.find(
            (processActivity) => processActivity.id === currentProcessActivityId
        );
        const lastFollowUpActivity = followUpActivities
            .filter((processActivity) => processActivity.id !== currentProcessActivityId)
            .at(-1);

        const nextFollowUpMoments = determineFollowUpMoments({
            currentFollowUpMoment: currentFollowUpActivity?.customControlData.moment ?? undefined,
            lastFollowUpMoment: lastFollowUpActivity?.customControlData.moment ?? undefined,
            wasPresentAtLastFollowUp: lastFollowUpActivity?.customControlData.aanwezigheid === PatientPresence.present,
        });

        if (nextFollowUpMoments.length === 1) {
            onChange({ moment: nextFollowUpMoments[0] });
        }
        setFollowUpMoments(nextFollowUpMoments);
    }
}
